import { fetchEntity, fetchEntityIfNeeded } from '@classpass/saga-tools';
import { all, call, select, spawn, takeEvery, put } from 'redux-saga/effects';
import { deepExtract } from 'helpers/deep';
import { ga } from 'helpers/multitrack';
import { trackOptimizelyEvent } from 'helpers/optimizely';
import { segment } from 'helpers/segment';
import { set as setUserType } from 'helpers/userTypeCookie';
import * as utmValues from 'helpers/utmValues';
import { waitlistRequest, glsLocation } from 'redux/saga/entities/bound';
import { selectUserLocale } from 'redux/modules/locale';
import { selectCmsPath } from 'redux/saga/selectors/cms';
import { persistUserLocation } from 'redux/saga/helpers/location';
import { setMsaId } from 'redux/modules/features/homepage';

export const CODE_BY_ERROR = Object.freeze({
  ALREADY_MEMBER: 4405,
  ALREADY_ON_WAITLIST: 4401,
  INVALID_EMAIL: 4402,
});

const LEAD_GEN_JOIN_WAITLIST = 'LEAD_GEN_JOIN_WAITLIST';
export const joinWaitlist = ({ email, location, submissionType }) => ({
  type: LEAD_GEN_JOIN_WAITLIST,
  email,
  location,
  submissionType,
});

export function* createLead(client, email, { locale, location, submissionType }) {
  const utms = yield call(utmValues.getForApi);

  yield call(fetchEntity, waitlistRequest, [
    client,
    {
      conversion: utms,
      country_code: deepExtract('msa.country.code', location),
      email,
      gls_location_id: deepExtract('location.id', location),
      locale,
      msa_id: deepExtract('msa.id', location),
      submission_type: submissionType,
    },
  ]);

  yield call(setUserType, 'lead');
  yield call(trackOptimizelyEvent, 'lead_capture');
}

export function* doJoinWaitlist(client, { email, location: { lat, lon }, submissionType }) {
  try {
    const locale = yield select(selectUserLocale);
    const location = yield call(fetchEntityIfNeeded, glsLocation, [client, lat, lon]);
    yield call(createLead, client, email, { locale, location, submissionType });
    const path = yield select(selectCmsPath);
    yield all([
      call(segment.track, 'successful_submission', {
        category: 'Lead Submission',
        label: path,
        value: 1,
      }),
      spawn(ga, 'Lead Submission', 'successful_submission', path, 1),
      call(persistUserLocation, lat, lon),
      put(setMsaId(deepExtract('msa.id', location))),
    ]);
  } catch (err) {
    const errorCode = deepExtract('code', err);
    const path = yield select(selectCmsPath);

    if (CODE_BY_ERROR.ALREADY_ON_WAITLIST === errorCode) {
      yield call(setUserType, 'lead');
      yield call(segment.track, 'duplicate_lead_error', {
        category: 'Lead Submission',
        label: path,
        value: 1,
      });
      yield spawn(ga, 'Lead Submission', 'duplicate_lead_error', path, 1);
    } else if (CODE_BY_ERROR.ALREADY_MEMBER === errorCode) {
      yield call(setUserType, 'registered');
      yield call(segment.track, 'email_associated_account_error', {
        category: 'Lead Submission',
        label: path,
        value: 1,
      });
      yield spawn(ga, 'Lead Submission', 'email_associated_account_error', path, 1);
    } else if (CODE_BY_ERROR.INVALID_EMAIL === errorCode) {
      yield call(segment.track, 'invalid_email_error', {
        category: 'Lead Submission',
        label: path,
        value: 1,
      });
      yield spawn(ga, 'Lead Submission', 'invalid_email_error', path, 1);
    }
  }
}

export function* watchLeadGenFormSagas(client) {
  yield takeEvery(LEAD_GEN_JOIN_WAITLIST, doJoinWaitlist, client);
}
